import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardMedia, Typography, Box, IconButton, Tooltip, Button } from '@mui/material';
import { Movie } from '../types/Movie';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { setMovieStatus } from '../services/movieApi';
import PopoverMenu from './common/PopoverMenu';
import ConfirmDialog from './dialogs/ConfirmDialog';
import MovieReviewDialog from './MovieReviewDialog';

interface MovieCardProps {
  movie: Movie;
  initialStatus?: number | null;
  initialScore?: number | null;
  initialSubStatus?: number[] | null;
  initialStartDate?: string | null;
  initialWatchMedium?: number[] | null;
  mode?: 'search' | 'library' | 'public';
}

const MovieCard: React.FC<MovieCardProps> = ({
  movie,
  initialStatus,
  initialScore,
  initialSubStatus,
  initialStartDate,
  initialWatchMedium,
  mode = 'search',
}) => {
  const navigate = useNavigate();
  // 映画ライブラリは全ユーザーに開放
  const hasEarlyAccess = true;
  const [status, setStatus] = useState<number | null>(initialStatus ?? null);
  const [score, setScore] = useState<number | null>(initialScore ?? null);
  const [subStatus, setSubStatus] = useState<number[] | null>(initialSubStatus ?? null);
  const watchMedium = initialWatchMedium ?? null;
  const [startDate, setStartDate] = useState<string | null>(initialStartDate ?? null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [isLoggedIn] = useState(() => !!localStorage.getItem('token'));

  React.useEffect(() => {
    setStatus(initialStatus ?? null);
    setScore(initialScore ?? null);
    setSubStatus(initialSubStatus ?? null);
    setStartDate(initialStartDate ?? null);
  }, [initialStatus, initialScore, initialSubStatus, initialStartDate]);

  const displayTitle = movie.japanese_name || movie.title;

  // 公開前かどうか（日付ベースで判定）
  const isUpcoming = React.useMemo(() => {
    if (!movie.release_date) return false;
    const releaseDate = new Date(movie.release_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return releaseDate > today;
  }, [movie.release_date]);
  const isWishlisted = status === 5;

  const handleWishlistClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoggedIn) { navigate('/login'); return; }
    if (!hasEarlyAccess) return;
    try {
      const newStatus = isWishlisted ? null : 5;
      await setMovieStatus(movie.movie_id, newStatus);
      setStatus(newStatus);
    } catch (error) {
      console.error('ウォッチリストの更新に失敗しました', error);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8.5) return '#4CAF50';
    if (score >= 7.5) return '#8BC34A';
    if (score >= 6.5) return '#CDDC39';
    if (score >= 5.5) return '#FFC107';
    if (score >= 4.5) return '#FF9800';
    return '#F44336';
  };

  const displayYear = movie.release_date
    ? new Date(movie.release_date).getFullYear()
    : null;

  const getStatusIcon = (statusCode: number | null) => {
    switch (statusCode) {
      case 1: return <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'primary.main' }} />;
      case 2: return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
      case 3: return <PauseCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      case 4: return <BlockIcon fontSize="small" sx={{ color: '#b99a71' }} />;
      case 5: return <StarBorderIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
      default: return <AddCircleOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 1: return '視聴済み';
      case 2: return '視聴中';
      case 3: return '中断';
      case 4: return '視聴中止';
      case 5: return 'ウォッチリスト';
      default: return 'ステータスを設定';
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (mode === 'public') return;
    setAnchorEl(event.currentTarget);
    setIsStatusChanging(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsStatusChanging(false);
  };

  const handleStatusChange = async (newStatus: number | null) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    if (newStatus === null) {
      setOpenDeleteConfirm(true);
      return;
    }
    try {
      await setMovieStatus(movie.movie_id, newStatus);
      const prevStatus = status;
      setStatus(newStatus);

      // 初回のステータス設定時（null→1）のみレビューダイアログを表示（検索モードのみ）
      if (mode === 'search' && prevStatus === null && newStatus === 1) {
        setOpenReviewDialog(true);
      }
    } catch (error) {
      console.error('ステータスの更新に失敗しました', error);
    }
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await setMovieStatus(movie.movie_id, null);
      setStatus(null);
    } catch (error) {
      console.error('ステータスの削除に失敗しました', error);
    }
    setOpenDeleteConfirm(false);
    handleClose();
  };

  const renderScore = () => {
    if (!movie.vote_average) return null;
    const score = Number(movie.vote_average);
    if (isNaN(score)) return null;
    const scoreColor = getScoreColor(score);

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 2, sm: 4 },
          right: { xs: 2, sm: 4 },
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          borderRadius: '6px',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${scoreColor}33`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: scoreColor,
            fontWeight: 'bold',
            fontSize: '0.7rem',
            lineHeight: 1,
          }}
        >
          {score.toFixed(1)}
        </Typography>
      </Box>
    );
  };

  const WATCH_MEDIUM_LABELS: Record<number, string> = {
    1: '劇場',
    2: '再上映',
    3: 'TV',
    4: 'VOD',
    5: 'パッケージ',
    6: '試写会',
  };

  const renderWatchMediumInfo = () => {
    if ((mode !== 'library' && mode !== 'public') || !watchMedium || watchMedium.length === 0) return null;

    const text = watchMedium.map(id => WATCH_MEDIUM_LABELS[id]).filter(Boolean).join(', ');
    if (!text) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          maxWidth: { xs: '45%', sm: '70%' },
          bottom: 4,
          left: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '4px',
          padding: '0px 4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontSize: { xs: '0.5rem', sm: '0.6rem' },
            fontWeight: 'medium',
            lineHeight: 1.2,
            opacity: 0.9,
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };

  const renderRewatchIcon = () => {
    if ((mode !== 'library' && mode !== 'public') || !subStatus || !subStatus.includes(1)) return null;

    return (
      <Box
        sx={{
          background: 'linear-gradient(45deg, #f0f0f0d8, #bebebed2)',
          borderRadius: '6px',
          padding: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        <Tooltip title="再視聴" arrow>
          <RefreshIcon sx={{ color: '#383838', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        </Tooltip>
      </Box>
    );
  };

  const renderUserScore = () => {
    if (score === null || score === undefined || (mode !== 'library' && mode !== 'public')) return null;

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '6px',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <StarIcon sx={{ color: 'gold', fontSize: '0.9rem', mr: 0.5 }} />
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {typeof score === 'number' ? score.toFixed(1) : score}
        </Typography>
      </Box>
    );
  };

  const renderPlayInfo = () => {
    if (mode !== 'library' && mode !== 'public') return null;

    if (startDate) {
      const d = new Date(startDate);
      const formattedDate = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <PlayCircleOutlineIcon sx={{ mr: 0.25, color: 'text.secondary', flexShrink: 0, fontSize: { xs: '0.85rem', sm: '1rem' } }} />
          <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formattedDate}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant="caption" color="text.secondary">
        {displayYear || '不明'}
      </Typography>
    );
  };

  const menuOptions = [
    { icon: <CheckCircleOutlineIcon />, label: '視聴済み', onClick: () => handleStatusChange(1), iconColor: 'primary.main', disabled: !hasEarlyAccess },
    { icon: <BlockIcon />, label: '視聴中止', onClick: () => handleStatusChange(4), iconColor: '#b99a71', disabled: !hasEarlyAccess },
    { icon: <StarBorderIcon />, label: 'ウォッチリスト', onClick: () => handleStatusChange(5), iconColor: 'secondary.main', disabled: !hasEarlyAccess },
    ...(status !== null ? [{
      icon: <CancelOutlinedIcon />, label: '削除', onClick: () => handleStatusChange(null), iconColor: 'error.main', disabled: !hasEarlyAccess
    }] : []),
  ];

  const eaFooter = !hasEarlyAccess ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.75 }}>
      <LockOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
        映画ライブラリはアーリーアクセス期間中です
      </Typography>
    </Box>
  ) : undefined;

  return (
    <>
      <Box
        component={Link}
        to={`/movie/${movie.movie_id}`}
        sx={{ textDecoration: 'none', display: 'block', height: '100%' }}
        onClick={(e) => {
          if (isStatusChanging) { e.preventDefault(); return; }
        }}
      >
        <Card
          elevation={0}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: { xs: 'none', lg: 'translateY(-3px)' } },
            backgroundColor: 'background.default',
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <CardMedia
            component="div"
            sx={{
              height: 0,
              paddingTop: '133.33%',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '4px',
              position: 'relative',
            }}
            image={movie.poster_url || '/image/no_image.png'}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {renderRewatchIcon()}
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}
            >
              {renderUserScore()}
            </Box>
            {renderWatchMediumInfo()}
          </CardMedia>
          <CardContent sx={{ flexGrow: 1, p: 0, pt: 1, backgroundColor: 'background.default', '&:last-child': { pb: 1 } }}>
            <Typography
              variant="subtitle2"
              component="div"
              noWrap
              sx={{ fontSize: { xs: '0.65rem', sm: '0.725rem' }, fontWeight: '600', mb: 0 }}
            >
              {displayTitle}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minWidth: 0 }}>
              {(mode === 'library' || mode === 'public') ? renderPlayInfo() : (
                <Typography variant="caption" color="text.secondary">
                  {displayYear || '不明'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                {mode === 'search' && !!movie.runtime && movie.runtime > 0 && !isUpcoming && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {movie.runtime}分
                  </Typography>
                )}
                {isUpcoming ? (
                  <Tooltip title={!hasEarlyAccess ? '映画ライブラリはアーリーアクセス期間中です' : ''} arrow>
                  <span>
                  <Button
                    size="small"
                    onClick={handleWishlistClick}
                    disabled={!hasEarlyAccess}
                    sx={{
                      height: { xs: '20px', sm: '22px' },
                      minWidth: { xs: '60px', sm: '68px' },
                      padding: { xs: '2px 6px', sm: '3px 8px' },
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      lineHeight: 1.2,
                      textTransform: 'none',
                      color: isWishlisted ? 'primary.main' : 'pulse.info',
                      backgroundColor: isWishlisted ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        color: isWishlisted ? 'primary.main' : 'primary.main',
                      },
                    }}
                  >
                    {isWishlisted ? 'watchlisted' : '+watchlist'}
                  </Button>
                  </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={getStatusText()} arrow>
                    <IconButton size="small" onClick={handleClick} sx={{ p: 0 }}>
                      {getStatusIcon(status)}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </CardContent>
          <PopoverMenu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            options={menuOptions}
            footer={eaFooter}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ ml: 1, mt: 1 }}
          />
        </Card>
      </Box>
      <ConfirmDialog
        open={openDeleteConfirm}
        title="ステータス削除の確認"
        message="ステータスを削除しますか？"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDeleteConfirm(false)}
        confirmText="削除"
        cancelText="キャンセル"
      />
      {openReviewDialog && (
        <MovieReviewDialog
          open={openReviewDialog}
          onClose={() => setOpenReviewDialog(false)}
          movieId={movie.movie_id}
          initialData={{
            score: null, reviewText: null, scoreTags: [],
            scoreStory: null, scoreVisual: null, scoreSound: null,
            scoreActing: null, scoreDirection: null,
            watchMedium: [], watchStyle: [],
            startDate: null, endDate: null, rewatch: false,
          }}
          releaseDate={movie.release_date}
          onReviewSaved={() => setOpenReviewDialog(false)}
        />
      )}
    </>
  );
};

export default MovieCard;
